import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

export default function QuadraticOptimizationPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        W optymalizacji szukamy największej albo najmniejszej wartości. Jeżeli
        rozważana wielkość jest opisana funkcją kwadratową, ekstremum odczytujemy
        z jej wierzchołka <InlineMath math="S=(p,q)" />.
      </p>
      <BlockMath math={String.raw`p=-\frac{b}{2a}\qquad q=f(p)`} />
      <p>
        <InlineMath math="p" /> jest <strong>argumentem</strong>, dla którego
        ekstremum występuje, natomiast <InlineMath math="q" /> jest
        <strong>największą lub najmniejszą wartością</strong>. Dla
        <InlineMath math="a>0" /> otrzymujemy minimum, a dla
        <InlineMath math="a<0" /> maksimum.
      </p>
      <p>
        Na przedziale domkniętym <InlineMath math="[L,R]" /> porównujemy wartości
        <InlineMath math="f(L)" /> i <InlineMath math="f(R)" /> oraz
        <InlineMath math="f(p)" />, jeśli <InlineMath math="p\in[L,R]" />.
      </p>
      <p>W zadaniu tekstowym pracujemy w stałej kolejności:</p>
      <ol style={{ paddingLeft: "1.4rem", marginTop: 0 }}>
        <li>oznaczamy niewiadomą i zapisujemy jej dopuszczalny zakres,</li>
        <li>budujemy funkcję opisującą pole, przychód albo zysk,</li>
        <li>liczymy wierzchołek i sprawdzamy, czy argument jest dopuszczalny,</li>
        <li>wracamy do treści i podajemy odpowiedź z jednostką.</li>
      </ol>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Argument i wartość maksimum",
      description: "Rozróżniamy współrzędne p i q wierzchołka.",
      content: (
        <div className="space-y-3">
          <BlockMath math="f(x)=-2x^2+8x+1" />
          <BlockMath math={String.raw`p=-\frac{8}{2\cdot(-2)}=2`} />
          <BlockMath math={String.raw`q=f(2)=-2\cdot2^2+8\cdot2+1=9`} />
          <p>
            Największa wartość funkcji wynosi <InlineMath math="9" /> i jest
            osiągana dla argumentu <InlineMath math="x=2" />.
          </p>
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: "Ekstremum na przedziale",
      description: "Porównujemy wierzchołek z oboma końcami przedziału.",
      content: (
        <div className="space-y-3">
          <p>Dla <InlineMath math="f(x)=x^2-4x+1" /> i przedziału <InlineMath math="[0,5]" /> mamy <InlineMath math="p=2" />.</p>
          <BlockMath math={String.raw`f(0)=1\qquad f(2)=-3\qquad f(5)=6`} />
          <p>Najmniejsza wartość to <InlineMath math="-3" />, a największa to <InlineMath math="6" />.</p>
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Modelowanie pola prostokąta",
      description: "Z warunku budujemy funkcję jednej zmiennej i maksymalizujemy ją.",
      content: (
        <div className="space-y-3">
          <p>Prostokąt ma obwód 40. Jeśli jeden bok ma długość <InlineMath math="x" />, drugi ma długość <InlineMath math="20-x" />.</p>
          <BlockMath math={String.raw`P(x)=x(20-x)=-x^2+20x`} />
          <BlockMath math={String.raw`p=-\frac{20}{2\cdot(-1)}=10`} />
          <BlockMath math="P(10)=100" />
          <p>Największe pole wynosi 100 i otrzymujemy je dla kwadratu o boku 10.</p>
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
